import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DateCalendarComponent } from '../date-time-field/date-calendar.component';
import { isoDateToDisplay, normalizeDisplayDate, toIsoDate } from '../date-time-field/date-time-field.util';

@Component({
  selector: 'lib-date-picker',
  standalone: true,
  imports: [DateCalendarComponent],
  template: `
    <div class="date-picker">
      <label class="date-picker__label" [attr.for]="inputId">{{ label }}</label>
      <div class="date-picker__control">
        <input
          [id]="inputId"
          class="date-picker__input"
          type="text"
          inputmode="numeric"
          autocomplete="off"
          [value]="displayValue"
          [placeholder]="placeholder"
          [disabled]="disabled"
          (input)="onInputChange($event)"
          (blur)="onInputBlur()"
        />
        <button
          type="button"
          class="date-picker__toggle"
          aria-label="Open calendar"
          [disabled]="disabled"
          (click)="toggleCalendar()"
        >
          <span class="date-picker__icon" aria-hidden="true"></span>
        </button>
        @if (isCalendarOpen) {
          <div class="date-picker__popover">
            <lib-date-calendar
              [selectedDate]="selectedIsoDate"
              [min]="min"
              [max]="max"
              (selectedDateChange)="selectDate($event)"
            />
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .date-picker {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
    }

    .date-picker__label {
      font-size: 12px;
      color: #374151;
      font-weight: 600;
      white-space: nowrap;
    }

    .date-picker__control {
      position: relative;
      display: grid;
      grid-template-columns: minmax(140px, 1fr) 32px;
      height: 32px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      box-sizing: border-box;
      background: #ffffff;
    }

    .date-picker__input {
      min-width: 0;
      border: none;
      border-radius: 8px 0 0 8px;
      padding: 0 10px;
      box-sizing: border-box;
      font-size: 12px;
      background: transparent;
      color: #111827;
      outline: none;
    }

    .date-picker__toggle {
      display: grid;
      place-items: center;
      border: none;
      border-left: 1px solid #e5e7eb;
      border-radius: 0 8px 8px 0;
      background: #f8fafc;
      color: #475569;
      cursor: pointer;
    }

    .date-picker__icon {
      position: relative;
      width: 13px;
      height: 12px;
      border: 1.5px solid currentColor;
      border-radius: 3px;
      box-sizing: border-box;
    }

    .date-picker__icon::before {
      content: '';
      position: absolute;
      top: 3px;
      left: -1.5px;
      right: -1.5px;
      border-top: 1.5px solid currentColor;
    }

    .date-picker__toggle:disabled {
      opacity: 0.65;
      cursor: default;
    }

    .date-picker__popover {
      position: absolute;
      z-index: 40;
      top: calc(100% + 6px);
      left: 0;
    }
  `],
})
export class DatePickerComponent implements OnChanges {
  @Input() public label = 'Date';
  @Input() public value = '';
  @Input() public min: string | null = null;
  @Input() public max: string | null = null;
  @Input() public disabled = false;
  @Input() public inputId = `date-picker-${Math.random().toString(36).slice(2, 10)}`;
  @Input() public placeholder = 'dd.mm.yyyy';

  @Output() public readonly valueChange = new EventEmitter<string>();

  protected displayText = '';
  protected isCalendarOpen = false;

  protected get displayValue(): string {
    return this.displayText;
  }

  protected get selectedIsoDate(): string {
    return this.clampDate(toIsoDate(this.displayText)) ?? '';
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.displayText = isoDateToDisplay(this.value);
    }
  }

  protected onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const nextValue = target?.value?.trim() ?? '';
    this.displayText = nextValue;

    if (!nextValue) {
      this.valueChange.emit('');
      return;
    }

    const isoValue = this.clampDate(toIsoDate(nextValue));
    if (isoValue) {
      this.valueChange.emit(isoValue);
    }
  }

  protected onInputBlur(): void {
    const normalized = normalizeDisplayDate(this.displayText);
    const isoValue = this.clampDate(toIsoDate(normalized));
    this.displayText = isoValue ? isoDateToDisplay(isoValue) : normalized;
    this.valueChange.emit(isoValue ?? '');
  }

  protected toggleCalendar(): void {
    this.isCalendarOpen = !this.isCalendarOpen;
  }

  protected selectDate(value: string): void {
    const isoValue = this.clampDate(value);
    this.displayText = isoValue ? isoDateToDisplay(isoValue) : '';
    this.isCalendarOpen = false;
    this.valueChange.emit(isoValue ?? '');
  }

  private clampDate(value: string | null): string | null {
    if (!value) {
      return null;
    }

    if (this.min && value < this.min) {
      return this.min;
    }

    if (this.max && value > this.max) {
      return this.max;
    }

    return value;
  }
}
